import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import type { FloorCell, FloorEscalation, FloorStation } from '../api'

/**
 * The Floor's rails.
 *
 * ## Everything here is the next thing somebody does
 *
 * The track answers "where are we". A rail on an operations board has to answer "so what do I
 * do", which on polling day is always one of three things: send a vehicle, ring a coordinator,
 * or leave it alone. So the rails are ordered by how soon they stop being fixable — escalations
 * past their due time, then cells nobody has heard from, then the transport gaps — and each row
 * carries the cell code somebody has to say out loud on the radio.
 */

export function Panel({ title, note, children, storageKey, maxBodyHeight = 0, tone }: {
  title: string
  note?: React.ReactNode
  children: React.ReactNode
  storageKey?: string
  maxBodyHeight?: number
  tone?: 'critical'
}) {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return true
    try {
      return window.localStorage.getItem(`axis.floor.${storageKey}`) !== 'closed'
    } catch {
      return true
    }
  })
  const toggle = () => setOpen((was) => {
    const next = !was
    if (storageKey) {
      try {
        window.localStorage.setItem(`axis.floor.${storageKey}`, next ? 'open' : 'closed')
      } catch { /* a preference is never worth failing the interaction over */ }
    }
    return next
  })
  const bodyId = `floor-${storageKey ?? title.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section className="border-b px-4 py-3" style={{ borderColor: 'var(--floor-grid)' }}>
      <header className="mb-2 flex items-baseline justify-between gap-2">
        {storageKey ? (
          <button type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId}
                  className="flex items-center gap-1.5 text-left">
            <span aria-hidden
                  className={cn('inline-block font-mono text-[8px] leading-none',
                    'transition-transform', open ? 'rotate-90' : 'rotate-0')}
                  style={{ color: 'var(--floor-ink-faint)' }}>▶</span>
            <h2 className="axis-floor-eyebrow"
                style={tone === 'critical' ? { color: 'var(--floor-critical)' } : undefined}>
              {title}
            </h2>
          </button>
        ) : (
          <h2 className="axis-floor-eyebrow">{title}</h2>
        )}
        {note ? <span className="axis-floor-mini tabular-nums">{note}</span> : null}
      </header>
      <div id={bodyId} hidden={!open}
           className={cn(maxBodyHeight > 0 && 'axis-floor-scroll overflow-y-auto')}
           style={maxBodyHeight > 0 ? { maxHeight: maxBodyHeight } : undefined}>
        {children}
      </div>
    </section>
  )
}

export function Stat({ label, value, tone, note, pulse }: {
  label: string
  value: React.ReactNode
  tone?: 'ahead' | 'behind' | 'critical' | 'queue'
  note?: string
  pulse?: boolean
}) {
  const ink = tone === 'behind' ? 'var(--floor-behind)'
    : tone === 'critical' ? 'var(--floor-critical)'
    : tone === 'queue' ? 'var(--floor-queue)'
    : tone === 'ahead' ? 'var(--floor-ahead)'
    : 'var(--floor-ink)'
  return (
    <div className="flex flex-col items-end gap-0.5" title={note}>
      <i className="axis-floor-eyebrow not-italic">{label}</i>
      <b className={cn('font-mono text-[16px] font-normal tabular-nums',
        pulse && 'axis-floor-throb')} style={{ color: ink }}>{value}</b>
    </div>
  )
}

/**
 * The countdown.
 *
 * Its own component so it can re-render every second without taking the board with it: the
 * track is a canvas driven by a frame loop, and putting this tick in the page's state would
 * rebuild the React tree around that canvas once a second for a number nothing else reads.
 */
export function Countdown({ closesAt, pollsOpen }: { closesAt: string, pollsOpen: boolean }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  const left = Math.floor((Date.parse(closesAt) - Date.now()) / 1000)
  if (!pollsOpen) {
    return (
      <span className="flex flex-col items-end gap-0.5">
        <i className="axis-floor-eyebrow not-italic">Polls</i>
        <b className="font-mono text-[16px] font-normal"
           style={{ color: 'var(--floor-ink-faint)' }}>
          {left > 0 ? 'not open yet' : 'closed'}
        </b>
      </span>
    )
  }
  const h = Math.floor(left / 3600)
  const m = Math.floor((left % 3600) / 60)
  const s = left % 60
  // The last hour is the one where a dispatch decision still changes an outcome, so it is the
  // one the clock is allowed to shout about.
  const urgent = left < 3600
  return (
    <span className="flex flex-col items-end gap-0.5">
      <i className="axis-floor-eyebrow not-italic">Polls close in</i>
      <b className={cn('font-mono text-[16px] font-normal tabular-nums',
        urgent && 'axis-floor-throb')}
         style={{ color: urgent ? 'var(--floor-critical)' : 'var(--floor-ink)' }}>
        {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
      </b>
    </span>
  )
}

// ── the rails ────────────────────────────────────────────────────────────────

/**
 * Escalations, worst-overdue first.
 *
 * The minutes late is the whole row: an escalation raised at nine and due at eleven is a
 * different object at half past eleven than it was at ten, and a list sorted by when it was
 * RAISED buries the ones that have gone bad.
 */
export function Escalations({ rows, onPick, selectedCellId }: {
  rows: FloorEscalation[]
  onPick: (cellId: string | null) => void
  selectedCellId: string | null
}) {
  if (!rows.length) {
    return <p className="axis-floor-mini py-2">Nothing open. The radio is quiet.</p>
  }
  return (
    <div className="flex flex-col">
      {rows.map((row) => {
        const late = row.minutesLate !== null && row.minutesLate > 0
        return (
          <button
            key={row.id}
            type="button"
            onClick={() => onPick(selectedCellId === row.cellId ? null : row.cellId)}
            className={cn('axis-floor-row', selectedCellId === row.cellId && 'axis-floor-row-on')}
          >
            <span className="flex items-baseline gap-2">
              <em className="axis-floor-code not-italic">{row.cellCode ?? '—'}</em>
              <span className="flex-1 truncate text-[11.5px]"
                    style={{ color: 'var(--floor-ink)' }}>{row.triggerRule}</span>
              <em className="font-mono text-[10px] not-italic tabular-nums"
                  style={{ color: late ? 'var(--floor-critical)' : 'var(--floor-ink-faint)' }}>
                {row.minutesLate === null
                  ? 'no due'
                  : late ? `+${row.minutesLate}m` : `${-row.minutesLate}m`}
              </em>
            </span>
            <span className="axis-floor-mini mt-0.5 block">
              {row.stationName ?? 'unknown station'} · {row.statusCode.toLowerCase()}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** Cells nobody has heard from, and cells with people who cannot get there. */
export function Attention({ cells, onPick, selectedCellId }: {
  cells: FloorCell[]
  onPick: (cellId: string | null) => void
  selectedCellId: string | null
}) {
  if (!cells.length) {
    return <p className="axis-floor-mini py-2">Every cell has reported recently.</p>
  }
  return (
    <div className="flex flex-col">
      {cells.map((cell) => (
        <button
          key={cell.id}
          type="button"
          onClick={() => onPick(selectedCellId === cell.id ? null : cell.id)}
          className={cn('axis-floor-row', selectedCellId === cell.id && 'axis-floor-row-on')}
          title={`${cell.members} members · ${cell.voted} voted · ${cell.outstanding} out`}
        >
          <span className="flex items-baseline gap-2">
            <em className="axis-floor-code not-italic">{cell.code}</em>
            <span className="flex-1 truncate text-[11.5px]"
                  style={{ color: 'var(--floor-ink)' }}>
              {cell.label ?? cell.stationName ?? 'unnamed'}
            </span>
            <em className="font-mono text-[10px] not-italic tabular-nums"
                style={{ color: 'var(--floor-behind)' }}>{cell.outstanding} out</em>
          </span>
          <span className="axis-floor-mini mt-0.5 flex items-center gap-2">
            {cell.stalledMinutes !== null ? <span>no report {cell.stalledMinutes}m</span> : null}
            {cell.transportGaps > 0 ? (
              <span style={{ color: 'var(--floor-behind)' }}>
                {cell.transportGaps} no transport
              </span>
            ) : null}
          </span>
        </button>
      ))}
    </div>
  )
}

export function Stations({ stations, selectedId, onPick }: {
  stations: FloorStation[]
  selectedId: string | null
  onPick: (id: string | null) => void
}) {
  return (
    <div className="flex flex-col">
      {stations.map((station) => (
        <button
          key={station.id}
          type="button"
          onClick={() => onPick(selectedId === station.id ? null : station.id)}
          className={cn('axis-floor-row', selectedId === station.id && 'axis-floor-row-on')}
        >
          <span className="flex items-baseline gap-2">
            <span className="flex-1 truncate text-[11.5px]"
                  style={{ color: 'var(--floor-ink)' }}>{station.name ?? 'Unnamed'}</span>
            <em className="font-mono text-[10px] not-italic tabular-nums"
                style={{ color: 'var(--floor-ink-faint)' }}>
              {Math.round(station.share * 100)}%
            </em>
          </span>
          <span className="mt-1 block h-[3px]" style={{ background: 'var(--floor-grid)' }}>
            <span className="block h-full axis-floor-grow"
                  style={{ width: `${station.share * 100}%`, background: 'var(--floor-ahead)' }} />
          </span>
        </button>
      ))}
    </div>
  )
}

/** The selected cell, with what somebody would need to say on the radio. */
export function CellCard({ cell, onOpen, onClear }: {
  cell: FloorCell
  onOpen: (id: string) => void
  onClear: () => void
}) {
  const done = cell.outstanding === 0
  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="axis-floor-code">{cell.code}</span>
        <span className="axis-floor-tag">{cell.statusCode.replaceAll('_', ' ')}</span>
        {cell.escalationsOpen > 0 ? (
          <span className="axis-floor-tag" style={{ color: 'var(--floor-critical)' }}>
            {cell.escalationsOpen} open
          </span>
        ) : null}
      </div>
      <h3 className="mb-1 text-[15px] leading-snug" style={{ color: 'var(--floor-ink)' }}>
        {cell.label ?? 'Unnamed cell'}
      </h3>
      <p className="axis-floor-mini mb-3">
        {cell.stationName ?? 'unknown station'}
        {cell.meetingPoint ? ` · meets at ${cell.meetingPoint}` : ''}
        {cell.coordinatorName ? ` · ${cell.coordinatorName}` : ''}
      </p>

      <div className="mb-3 grid grid-cols-2 gap-px" style={{ background: 'var(--floor-grid)' }}>
        <Cell label="Members" value={cell.members} />
        <Cell label="Voted" value={cell.voted} tone="ahead" />
        <Cell label="In queue" value={cell.inQueue} tone="queue" />
        <Cell label="Still out" value={cell.outstanding}
              tone={cell.outstanding > 0 ? 'behind' : undefined} />
      </div>

      <p className="mb-3 border-l pl-3 text-[11.5px] leading-relaxed"
         style={{
           color: 'var(--floor-ink-dim)',
           borderColor: done ? 'var(--floor-ahead)' : 'var(--floor-behind)',
         }}>
        {done
          ? 'Everybody on this roster has voted. Nothing to send.'
          : cell.perHourNeeded === null
            ? `${cell.outstanding} never made it before the close.`
            : `To finish before the close this cell has to move `
              + `${cell.perHourNeeded.toFixed(1)} members an hour. It is `
              + `${Math.round(Math.abs(cell.behind) * 100)} points `
              + `${cell.behind > 0 ? 'behind' : 'ahead of'} the day's pace.`}
      </p>

      <div className="flex flex-col gap-1.5">
        <button type="button" onClick={() => onOpen(cell.id)} className="axis-floor-button">
          Open the cell →
        </button>
        <button type="button" onClick={onClear} className="axis-floor-button">
          Clear selection
        </button>
      </div>
    </>
  )
}

function Cell({ label, value, tone }: {
  label: string
  value: number
  tone?: 'ahead' | 'behind' | 'queue'
}) {
  const ink = tone === 'behind' ? 'var(--floor-behind)'
    : tone === 'queue' ? 'var(--floor-queue)'
    : tone === 'ahead' ? 'var(--floor-ahead)'
    : 'var(--floor-ink)'
  return (
    <div className="px-2.5 py-2" style={{ background: 'var(--floor-bg-edge)' }}>
      <i className="axis-floor-eyebrow not-italic mb-1 block">{label}</i>
      <b className="font-mono text-[15px] font-normal tabular-nums"
         style={{ color: ink }}>{value}</b>
    </div>
  )
}
